import React from 'react';
import { useTranslation } from 'react-i18next';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { keyMap } from '@locales/lokalise/keyMap';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { Select } from 'antd';
import cn from 'classnames';
import { CONSTANTS } from 'components/shared/constants';
import { BaseOption } from 'components/shared/forms/forms.types';
import { Attributes } from '../formField.interface';
import styles from './styles.module.scss';

const { Option } = Select;

export type OptionValue = string | number;

type SelectedValue = OptionValue | undefined;

interface ISelectProps {
  name: string;
  title?: string;
  tooltip?: string;
  chooseText?: string;
  showChooseText: boolean;
  options: BaseOption<OptionValue>[];
  attributes?: Attributes;
  className?: string;
  valid?: boolean;
  validationMessage?: string;
  value: SelectedValue;
  hintMessage?: string;
  required?: boolean;
  renderBefore?: JSX.Element | null;
  renderAfter?: JSX.Element | null;
  onChange: (name: string, value: OptionValue) => void;
  labelClassName?: string;
}

const CustomSelect = (props: ISelectProps) => {
  const {
    name,
    title,
    tooltip,
    chooseText,
    showChooseText,
    options = [],
    attributes,
    className,
    valid,
    validationMessage,
    value,
    hintMessage,
    required,
    renderBefore = null,
    renderAfter = null,
    labelClassName,
  } = props;

  const { i18n } = useTranslation();

  const setOptions = (options: BaseOption<OptionValue>[]): Array<JSX.Element> =>
    options.map((option, idx) => {
      const optionTitle = `${option.name || option.value}`;
      const optionTitleToDisplay = i18n.exists(optionTitle) ? i18n.t(optionTitle as KeyMapKeys) : optionTitle;
      const optionValue = makeAntdFriendlyOption(option.value);

      return (
        <Option key={`${option.value}_${idx}`} value={optionValue}>
          {optionTitleToDisplay}
        </Option>
      );
    });

  const onChange = (value: string): void => {
    const { onChange } = props;

    if (showChooseText && value === CONSTANTS.DEFAULT_SELECT_OPTION_VALUE) {
      onChange(name, value);

      return;
    }

    const selectedOption = getSelectedOptionWithOriginalValueType(options, value);

    if (!selectedOption) {
      return;
    }

    onChange(name, selectedOption.value);
  };

  const defaultSelectOption = chooseText ? i18n.t(chooseText as KeyMapKeys) : ` -- ${i18n.t(keyMap.formsChoose)} -- `;

  const selectedValue = value !== undefined ? makeAntdFriendlyOption(value) : undefined;

  return (
    <InputFieldWrapper
      name={name}
      title={title}
      required={required}
      className={className}
      labelClassName={labelClassName}
      hintMessage={hintMessage}
      tooltip={tooltip}
      valid={valid}
      validationMessage={validationMessage}
      renderBefore={renderBefore}
      renderAfter={renderAfter}>
      <div className={cn('form__select-wrapper', styles.customSelect)}>
        <Select
          className={cn('form__input', { invalid: !valid, disabled: attributes?.disabled })}
          suffixIcon={null}
          value={selectedValue}
          onChange={onChange}
          popupMatchSelectWidth={true}
          getPopupContainer={(trigger) => trigger.parentNode}
          popupClassName={styles.customSelect__popup}
          {...attributes}>
          {showChooseText && <Option value={CONSTANTS.DEFAULT_SELECT_OPTION_VALUE}>{defaultSelectOption}</Option>}
          {setOptions(options)}
        </Select>
      </div>
    </InputFieldWrapper>
  );
};

const makeAntdFriendlyOption = (value: OptionValue) => String(value);

const getSelectedOptionWithOriginalValueType = (options: Array<BaseOption<OptionValue>>, value: string) =>
  options.find((option) => makeAntdFriendlyOption(option.value) == value);

export default CustomSelect;
